/**
 * Full-size viewer for a market's screenshots, with paging and download.
 *
 * Shared by the header pill's modal and the conditions panel on capacity
 * request forms. Images are fetched as blobs rather than set as an <img src>,
 * because the endpoint needs the API client's auth header.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cachedImageUrl, loadImageUrl, prefetchImages } from '../services/marketConditionsImageCache';
import type { MarketNoteImage } from '../services/marketConditionsStore';
import ModalHeader from './ui/ModalHeader';

interface Props {
  market: string;
  images: MarketNoteImage[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** Render in place rather than portaling, for a host already in the top layer. */
  inPlace?: boolean;
}

export default function MarketConditionsImageViewer({ market, images, index, onIndexChange, onClose, inPlace = false }: Props) {
  const [preview, setPreview] = useState<{ id: number; url: string } | null>(null);
  const [failed, setFailed] = useState(false);
  const image = images[index];

  useEffect(() => {
    if (!image) return;
    const ready = cachedImageUrl(image.id);
    if (ready) { setPreview({ id: image.id, url: ready }); setFailed(false); return; }
    let cancelled = false;
    setPreview(null);
    setFailed(false);
    loadImageUrl(image.id)
      .then(url => { if (!cancelled) setPreview({ id: image.id, url }); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [image?.id]);

  // Warm the neighbours so Previous/Next do not blank between images.
  useEffect(() => {
    if (images.length < 2) return;
    prefetchImages([
      images[(index + 1) % images.length].id,
      images[(index - 1 + images.length) % images.length].id,
    ]);
  }, [images, index]);

  const move = (step: number) => onIndexChange((index + step + images.length) % images.length);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.stopPropagation(); onClose(); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); move(1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, images.length, onClose]);

  const download = () => {
    if (!image || preview?.id !== image.id) return;
    const link = document.createElement('a');
    link.href = preview.url;
    link.download = image.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  if (!image) return null;

  const viewer = <div role="dialog" aria-modal="true" aria-label={`${market} images`}
    className={`${inPlace ? 'absolute' : 'fixed'} inset-0 z-[10000] flex items-center justify-center bg-scrim/80 p-3`}
    onClick={onClose}>
    <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-cmp-modal shadow-2xl" onClick={event => event.stopPropagation()}>
      <ModalHeader title={`${market} images · ${index + 1} of ${images.length}`} onClose={onClose} closeLabel="Close image viewer"
        actions={<button type="button" onClick={download} disabled={preview?.id !== image.id}
          className="rounded-md px-2 py-1 text-sm font-medium text-cmp-modal-header-fg hover:bg-white/20 disabled:opacity-50">Download</button>} />
      <div className="flex min-h-64 flex-1 items-center justify-center overflow-auto bg-black/90 p-3">
        {preview?.id === image.id
          ? <img src={preview.url} alt={image.name} className="max-h-[70vh] max-w-full object-contain" />
          : <p className="text-sm text-white">{failed ? 'Could not load image.' : 'Loading image…'}</p>}
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-theme-border-primary px-4 py-3">
        <button type="button" onClick={() => move(-1)} disabled={images.length < 2} className="rounded-md border border-theme-border-primary px-3 py-1.5 text-sm text-heading disabled:opacity-50">Previous</button>
        <span className="min-w-0 truncate text-sm text-muted">{image.name}</span>
        <button type="button" onClick={() => move(1)} disabled={images.length < 2} className="rounded-md border border-theme-border-primary px-3 py-1.5 text-sm text-heading disabled:opacity-50">Next</button>
      </div>
    </div>
  </div>;

  return inPlace ? viewer : createPortal(viewer, document.body);
}
